import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelCampaign, fetchCampaign, fetchCampaigns, pauseCampaign, resumeCampaign,
} from "@services/reactivation";

const POLL_MS = 10000;
const LIVE = ["running"];

const CONTROLS = {
  pause: pauseCampaign,
  resume: resumeCampaign,
  cancel: cancelCampaign,
};

/**
 * Campaign list and detail for one clinic.
 *
 * Polls only while a campaign is queued or running, the same rule
 * useAgentSimRuns uses, so a settled clinic makes no background requests.
 */
export function useReactivationCampaigns(clinicId) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Pause/Resume/Cancel only exist inside the drawer, which is an opaque
  // overlay over the page banner, so their failures need their own channel.
  const [controlError, setControlError] = useState(null);
  // Read inside the poll without making it a dependency, which would restart
  // the interval on every tick.
  const openIdRef = useRef(null);
  // Every request is stamped; a resolution whose stamp is no longer the
  // current one belongs to a clinic the user has left or a drawer they have
  // closed, and is thrown away instead of overwriting what is on screen.
  const listReqRef = useRef(0);
  const detailReqRef = useRef(0);

  const clearError = useCallback(() => setError(null), []);
  const clearControlError = useCallback(() => setControlError(null), []);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    const reqId = ++listReqRef.current;
    if (!quiet) setLoading(true);
    try {
      const rows = await fetchCampaigns(clinicId);
      if (listReqRef.current !== reqId) return;
      setCampaigns(rows);
      setError(null);
    } catch (e) {
      if (listReqRef.current !== reqId) return;
      setError(e.message);
    } finally {
      if (!quiet && listReqRef.current === reqId) setLoading(false);
    }
  }, [clinicId]);

  const loadDetail = useCallback(async (campaignId, { quiet = false } = {}) => {
    const reqId = ++detailReqRef.current;
    if (!quiet) setDetailLoading(true);
    try {
      const next = await fetchCampaign(campaignId, clinicId);
      if (detailReqRef.current !== reqId) return;
      setDetail(next);
      setError(null);
    } catch (e) {
      if (detailReqRef.current !== reqId) return;
      setError(e.message);
    } finally {
      if (!quiet && detailReqRef.current === reqId) setDetailLoading(false);
    }
  }, [clinicId]);

  const openCampaign = useCallback(async (campaign) => {
    openIdRef.current = campaign.id;
    setControlError(null);
    await loadDetail(campaign.id);
  }, [loadDetail]);

  const closeCampaign = useCallback(() => {
    openIdRef.current = null;
    // Retire any detail request still in flight, or it would reopen the
    // drawer over whatever the user moved on to.
    detailReqRef.current += 1;
    setDetail(null);
    setDetailLoading(false);
    setControlError(null);
  }, []);

  const control = useCallback(async (action, campaignId) => {
    setControlError(null);
    try {
      await CONTROLS[action](campaignId, clinicId);
    } catch (e) {
      setControlError(e.message);
      return;
    }
    await refresh({ quiet: true });
    if (openIdRef.current === campaignId) await loadDetail(campaignId, { quiet: true });
  }, [clinicId, refresh, loadDetail]);

  useEffect(() => {
    // A new clinic invalidates everything in flight for the old one.
    listReqRef.current += 1;
    detailReqRef.current += 1;
    openIdRef.current = null;
    setCampaigns([]);
    setDetail(null);
    setError(null);
    setControlError(null);
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!campaigns.some((c) => LIVE.includes(c.status))) return undefined;
    const id = setInterval(() => {
      refresh({ quiet: true });
      if (openIdRef.current) loadDetail(openIdRef.current, { quiet: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [campaigns, refresh, loadDetail]);

  return {
    campaigns, loading, error, clearError, refresh,
    detail, detailLoading, openCampaign, closeCampaign,
    control, controlError, clearControlError,
  };
}
